import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CartResponse, PosSystemApi } from '../possystemapi';
import { PayPalButtons, PayPalScriptProvider, ReactPayPalScriptOptions } from '@paypal/react-paypal-js';
import ChargeItem from '../Models/ChargeItem';

interface CustomerDisplayCartProps {
    refreshInterval?: number;
}

interface OrderData {
    id: string;
    details?: Array<{
        issue: string;
        description: string;
    }>;
    debug_id?: string;
}

const CustomerDisplayCart: React.FC<CustomerDisplayCartProps> = ({ refreshInterval = 500 }) => {
    const urlParams = new URLSearchParams(window.location.search);
    const journeyid = urlParams.get('journeyid');

    const [cart, setCart] = useState<CartResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [noCartAvailable, setNoCartAvailable] = useState<boolean>(false);
    const [showReceiptDialog, setShowReceiptDialog] = useState<boolean>(false);
    const cartItemsRef = useRef<HTMLDivElement>(null);
    const prevItemCountRef = useRef<number>(0);
    const api = new PosSystemApi();

    const initialOptions: ReactPayPalScriptOptions = {
        clientId: "AbgY5446uwNu9VfFGPktyQoSe23N6UAyLjUb5_w3R-w-i2qFlDMo_1rYenWKIzpx1Vf3CqK3GqPA4c_t",
        currency: "EUR",
        intent: "authorize"
    };

    function createOrderS(data: any, actions: any, citems: ChargeItem[]) {
        console.log("createOrder  called", citems)

        return actions.order.create({
            intent: "AUTHORIZE",
            purchase_units: [
                {
                    amount: {
                        value: citems.reduce((total, item) => total + item.Amount, 0).toFixed(2).toString(),
                        currency_code: "EUR",
                    },
                },
            ]
        });
    }

    // Create a memoized function to fetch cart data
    const fetchCart = useCallback(async () => {
        if (!journeyid) return;

        try {
            setLoading(true);

            const response: CartResponse | null = await api.getCart(journeyid);

            if (response === null) {
                setCart(null);
                setError(null);
                setNoCartAvailable(true);
                return;
            }

            setCart(response);
            setNoCartAvailable(false);
            setError(null);
            
            // Show receipt dialog if there's a receipt URL available
            if (response.ReceiptURL) {
                setShowReceiptDialog(true);
            }
        } catch (err) {
            setError(`Failed to fetch cart: ${(err as Error).message}`);
        } finally {
            setLoading(false);
        }
    }, [journeyid]);

    useEffect(() => {
        if (!journeyid) {
            return;
        }
        fetchCart();
        const intervalId = setInterval(fetchCart, refreshInterval);
        return () => {
            clearInterval(intervalId);
        };
    }, [journeyid, refreshInterval, fetchCart]);    useEffect(() => {
        const currentItemCount = cart?.cbChargeItems?.length || 0;
        if (cartItemsRef.current && (currentItemCount > prevItemCountRef.current || currentItemCount === 1)) {
            cartItemsRef.current.scrollTop = cartItemsRef.current.scrollHeight;
        }
        prevItemCountRef.current = currentItemCount;
    }, [cart?.cbChargeItems]);
    
    // Function to open the receipt URL in a new tab
    const openReceiptInNewTab = () => {
        if (cart?.ReceiptURL) {
            window.open(cart.ReceiptURL, '_blank');
            setShowReceiptDialog(false);
        }
    };

    if (!loading && !cart && !noCartAvailable) {
        return (
            <div className="customer-cart">
                <div className="flex items-center justify-center h-full">
                    <div className="text-center p-8">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mb-4"></div>
                        <p className="text">Loading cart...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (cart === null) {
        return (
            <div className="customer-cart justify-center p-5 bg-slate-500">
                <div className="flex items-center justify-center h-full">
                    <div className="customer-cart-items bg-white shadow rounded p-4">
                        <div className="text-center p-8">
                            <div className="text-gray-500 mb-4">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h18v18H3z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h8" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-medium text-gray-900 mb-1">No Cart Available</h3>
                            <p className="text-gray-500">The cart you are trying to visit is not available.</p>
                        </div>
                    </div>
                </div>
            </div >
        );
    }

    const subtotalValue = cart?.cbChargeItems?.reduce((sum, item) => sum + item.Amount, 0) || 0;
    const totalPaymentsValue = cart?.cbPayItems?.reduce((sum, item) => sum + item.Amount, 0) || 0;
    let subtotal = subtotalValue;
    let totalPayments = totalPaymentsValue;
    let total = subtotalValue - totalPaymentsValue;
    return (
        <div className="customer-cart justify-center p-5 bg-slate-500">
            <div className="customer-cart-content justify-center">
                <div className="customer-cart-items bg-white shadow rounded p-4 lg:w-6/12 w-full">
                    <div className="h-full flex flex-col">
                        <div className="px-4 py-2 font-semibold border-b text "></div>
                        <div ref={cartItemsRef} className="overflow-y-auto flex-grow">
                            {cart?.cbChargeItems && cart.cbChargeItems.length > 0 ? (
                                cart.cbChargeItems.map((item, index) => (
                                    <div key={index} className="cart-item">
                                        <div className="flex-1">
                                            <div className="cart-item-name">{item.Description}</div>
                                            <div className="cart-item-quantity">
                                                {item.Quantity} x {(item.UnitPrice || (item.Amount / item.Quantity)).toFixed(2)}€
                                            </div>
                                            {item.VATRate > 0 && (
                                                <div className="text-xs text-gray-500">
                                                    VAT: {item.VATRate}%
                                                </div>
                                            )}
                                        </div>
                                        <div className="cart-item-price">
                                            {item.Amount.toFixed(2)}€
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-500 p-4">No items in cart</div>
                            )}
                        </div>

                        {cart?.cbPayItems && cart.cbPayItems.length > 0 && (
                            <div>
                                <div className="px-4 py-2 font-semibold border-b text">Payments</div>
                                {cart.cbPayItems.map((payment, index) => (
                                    <div key={index} className="cart-item">
                                        <div>{payment.Description}</div>
                                        <div className="font-semibold">{payment.Amount.toFixed(2)}€</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="cart-totals mt-auto">
                            <div className="cart-total-row">
                                <div className="font-medium">Subtotal:</div>
                                <div>{subtotal.toFixed(2)}€</div>
                            </div>
                            {totalPayments > 0 && (
                                <div className="cart-total-row text-green-700">
                                    <div className="font-medium">Payments:</div>
                                    <div>-{totalPayments.toFixed(2)}€</div>
                                </div>
                            )}
                            <div className="cart-total-row cart-grand-total">
                                <div>Total:</div>
                                <div>{total.toFixed(2)}€</div>
                            </div>
                        </div>

                        {/* <div className="mx-auto w-full max-w-lg h-56 mt-5">
                            <PayPalScriptProvider options={initialOptions}>
                                <PayPalButtons style={{ layout: "horizontal" }}
                                    createOrder={(o, a) => createOrderS(o, a, cart?.cbChargeItems || [])}
                                    onApprove={async (
                                        data,
                                        actions
                                    ) => {
                                        const orderData = await api.authorize(journeyid!, data.orderID);
                                        console.log(orderData);
                                    }} />
                            </PayPalScriptProvider>
                        </div> */}
                    </div>
                </div>
            </div>

            {showReceiptDialog && cart?.ReceiptURL && (
                <div className="fixed inset-0 flex items-center justify-center z-50">
                    <div className="bg-black bg-opacity-50 absolute inset-0"></div>
                    <div className="bg-white rounded-lg overflow-hidden shadow-lg max-w-sm w-full z-10">
                        <div className="p-4">
                            <h3 className="text-lg font-semibold mb-2">Receipt Available</h3>
                            <p className="text-gray-700 mb-4">A receipt is available for this transaction. Would you like to view it?</p>
                            <div className="flex justify-end space-x-2">
                                <button
                                    onClick={openReceiptInNewTab}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    View Receipt
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerDisplayCart;
